/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { DataTable } from '@/components/CustomTable'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Pencil, Trash2 } from 'lucide-react'
import { useDebounce } from '@/hooks/useDebounce'
import {
  useItemsQuery,
  useCreateItemMutation,
  useUpdateItemMutation,
  useDeleteItemMutation,
} from '@/hooks/useItems'
import { useCategorySelectQuery } from '@/hooks/useCategories'
import type { Item } from '@/types/backend'
import { toast } from 'sonner'
import type { ColumnDef } from '@tanstack/react-table'
import AppDialog, { ModalActions } from '@/components/CustomModal'
import Loading from '@/components/Loading'
import { Input } from '@/components/ui/input'
import { useTableUrlState } from '@/hooks/useTableUrlState'
import { ImportItemsDialog } from '@/components/items/ImportItemsDialog'

export default function ItemsPage() {
  const navigate = useNavigate()

  // --- URL <-> filtre catégorie
  const [searchParams, setSearchParams] = useSearchParams()
  const initialCategoryId = searchParams.get('categoryId') ?? undefined
  const [categoryIdFilter, setCategoryIdFilter] = useState<string | undefined>(
    initialCategoryId
  )

  const [importOpen, setImportOpen] = useState(false)

  useEffect(() => {
    const next = new URLSearchParams(searchParams)
    if (categoryIdFilter) {
      next.set('categoryId', categoryIdFilter)
    } else {
      next.delete('categoryId')
      next.delete('category') // si tu passais aussi un libellé
    }
    setSearchParams(next, { replace: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryIdFilter])

  // --- Table serveur (avec recherche intégrée au DataTable)
  const {
    state: { search, pageIndex, pageSize },
    setSearch,
    setPageIndex,
    setPageSize,
  } = useTableUrlState('items', { search: '', pageIndex: 0, pageSize: 20 })
  const debounced = useDebounce(search, 400)

  // --- Data
  const { data: cats } = useCategorySelectQuery(true)
  const {
    data: list,
    status,
    isFetching,
  } = useItemsQuery(
    {
      search: debounced || undefined,
      categoryId: categoryIdFilter,
      page: pageIndex + 1,
      pageSize,
    },
    true
  )

  const tableProps = {
    searchValue: search,
    onSearchChange: setSearch,
    currentPageIndex: pageIndex,
    currentPageSize: pageSize,
    onPageChange: setPageIndex,
    onPageSizeChange: setPageSize,
    paginationServer: true as const,
    sortingServer: false as const,
  }

  const total = list?.total ?? 0
  const items = list?.data ?? []
  const pageCount = useMemo(
    () => Math.max(1, Math.ceil(total / pageSize)),
    [total, pageSize]
  )

  // --- Modales / états locaux
  const [open, setOpen] = useState(false)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null)
  const [editing, setEditing] = useState<Item | null>(null)
  const [form, setForm] = useState<{
    name: string
    sku: string
    unitPrice: string // string pour l'input
    packaging: string
    categoryId: string // 'NONE' -> pas de catégorie
    type: 'PRODUCT' | 'SERVICE'
  }>({
    name: '',
    sku: '',
    unitPrice: '',
    packaging: '',
    categoryId: 'NONE',
    type: 'PRODUCT',
  })

  // --- Mutations
  const createMut = useCreateItemMutation()
  const updateMut = useUpdateItemMutation(editing?.id || '')
  const deleteMut = useDeleteItemMutation()

  // --- Colonnes
  const columns = useMemo<ColumnDef<Item>[]>(
    () => [
      {
        accessorKey: 'name',
        header: 'Nom',
        cell: ({ row }) => (
          <button
            onClick={() => navigate(`/items/${row.original.id}`)}
            className='font-medium underline-offset-2 hover:underline'
          >
            {row.original.name}
          </button>
        ),
      },
      {
        accessorKey: 'sku',
        header: 'SKU',
        cell: ({ row }) => row.original.sku || '—',
      },
      {
        accessorKey: 'type',
        header: 'Type',
        cell: ({ row }) => (
          <span className='inline-flex items-center rounded-full border px-2 py-0.5 text-xs'>
            {row.original.type === 'SERVICE' ? 'Service' : 'Produit'}
          </span>
        ),
      },
      {
        accessorKey: 'category',
        header: 'Catégorie',
        cell: ({ row }) => row.original.category?.name ?? '—',
      },
      {
        accessorKey: 'unitPrice',
        header: 'Prix unitaire',
        cell: ({ row }) =>
          Number(row.original.unitPrice).toLocaleString('fr-FR') + ' CFA',
      },
      {
        accessorKey: 'stockOnHand',
        header: 'Stock',
        cell: ({ row }) =>
          row.original.type === 'SERVICE' ? '—' : (row.original.stockOnHand ?? 0),
      },
      {
        id: 'actions',
        header: 'Actions',
        cell: ({ row }) => (
          <div className='flex items-center gap-3'>
            <Pencil
              size={18}
              className='cursor-pointer text-green-600 mr-4'
              onClick={(e) => {
                e.stopPropagation()
                e.preventDefault()
                setEditing(row.original)
                setForm({
                  name: row.original.name ?? '',
                  sku: row.original.sku ?? '',
                  unitPrice: String(row.original.unitPrice ?? ''),
                  packaging: row.original.packaging ?? '',
                  categoryId: row.original.category?.id ?? 'NONE',
                  type: row.original.type,
                })
                setOpen(true)
              }}
            />
            <Trash2
              size={18}
              className='cursor-pointer text-red-600 ml-4'
              onClick={(e) => {
                e.stopPropagation()
                e.preventDefault()
                setPendingDeleteId(row.original.id)
                setConfirmOpen(true)
              }}
            />
          </div>
        ),
      },
    ],
    [navigate]
  )

  // --- Actions
  const openCreate = () => {
    setEditing(null)
    setForm({
      name: '',
      sku: '',
      unitPrice: '',
      packaging: '',
      categoryId: 'NONE',
      type: 'PRODUCT',
    })
    setOpen(true)
  }

  const submit = async () => {
    const name = form.name.trim()
    const unitPrice = parseFloat((form.unitPrice || '').replace(',', '.'))

    if (!name) {
      toast.error('Nom requis')
      return
    }
    if (!isFinite(unitPrice) || unitPrice <= 0) {
      toast.error('Prix unitaire invalide', {
        description: 'Veuillez saisir un nombre > 0',
      })
      return
    }

    try {
      if (editing) {
        // En édition, null = vider le champ ; undefined le laisserait
        // inchangé côté backend (voir ItemsService.update()).
        await updateMut.mutateAsync({
          name,
          sku: form.sku.trim() || null,
          unitPrice,
          packaging: form.packaging.trim() || null,
          categoryId: form.categoryId !== 'NONE' ? form.categoryId : null,
        } as any)
        toast.success('Produit modifié')
      } else {
        await createMut.mutateAsync({
          name,
          sku: form.sku.trim() || undefined,
          unitPrice,
          packaging: form.packaging.trim() || undefined,
          categoryId: form.categoryId !== 'NONE' ? form.categoryId : undefined,
          type: form.type,
        })
        toast.success('Produit créé')
      }
      setOpen(false)
    } catch (e: any) {
      toast.error('Erreur', {
        description: e?.response?.data?.message ?? 'Action échouée',
      })
    }
  }

  const confirmDelete = async () => {
    if (!pendingDeleteId) return
    try {
      await deleteMut.mutateAsync(pendingDeleteId)
      toast.success('Produit supprimé')
      setConfirmOpen(false)
      setPendingDeleteId(null)
    } catch (e: any) {
      toast.error('Suppression échouée', {
        description: e?.response?.data?.message,
      })
    }
  }

  return (
    <div className='space-y-4'>
      <div className='flex items-center justify-between gap-3'>
        <h3 className='text-2xl font-semibold'>Produits</h3>
        <div className='flex items-center gap-2'>
          <Button onClick={openCreate}>Ajouter</Button>
          <Button variant='secondary' onClick={() => setImportOpen(true)}>
            Importer CSV
          </Button>
        </div>
      </div>

      {/* Barre d’outils : recherche + filtre catégorie */}
      <div className='flex flex-wrap items-center gap-3'>
        <div className='w-full sm:w-auto'>
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder='Rechercher un produit…'
            className='w-full sm:w-[260px]'
          />
        </div>

        <Select
          value={categoryIdFilter ?? 'ALL'}
          onValueChange={(v) =>
            setCategoryIdFilter(v === 'ALL' ? undefined : v)
          }
        >
          <SelectTrigger className='w-[220px]'>
            <SelectValue placeholder='Filtrer par catégorie' />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value='ALL'>Toutes les catégories</SelectItem>
            {(cats ?? []).map((c) => (
              <SelectItem key={c.id} value={c.id}>
                {c.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {status === 'pending' ? (
        <Loading />
      ) : (
        <>
          <div className='text-sm text-muted-foreground'>
            Total: <b>{total}</b>
          </div>
          <DataTable<Item, unknown>
            columns={columns}
            data={items}
            {...tableProps}
            total={total}
            pageCount={pageCount}
            searchValue={search}
            onSearchChange={setSearch}
            rowClickable
            onRowClick={(row) => navigate(`/items/${row.id}`)}
          />
          <div className='text-xs text-muted-foreground'>
            {isFetching ? 'Actualisation…' : null}
          </div>
        </>
      )}

      {/* --- Modale CRÉER / ÉDITER --- */}
      <AppDialog
        open={open}
        onOpenChange={setOpen}
        title={editing ? 'Modifier un produit' : 'Ajouter un produit'}
        description='Renseignez les champs requis. La catégorie est optionnelle.'
        asForm
        onSubmit={(e) => {
          e.preventDefault()
          submit()
        }}
        footer={
          <ModalActions
            onConfirm={submit}
            confirmLabel='Valider'
            confirmLoading={createMut.isPending || updateMut.isPending}
          />
        }
      >
        <div className='w-full px-4 py-2 space-y-3'>
          <div className='grid gap-1.5'>
            <Label>Nom</Label>
            <Input
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              placeholder='Ex: Coca 50cl'
            />
          </div>

          <div className='grid gap-1.5'>
            <Label>Type</Label>
            {editing ? (
              <div className='text-sm text-muted-foreground'>
                {form.type === 'SERVICE' ? 'Service' : 'Produit'}
              </div>
            ) : (
              <Select
                value={form.type}
                onValueChange={(v) =>
                  setForm((f) => ({ ...f, type: v as 'PRODUCT' | 'SERVICE' }))
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value='PRODUCT'>Produit</SelectItem>
                  <SelectItem value='SERVICE'>Service</SelectItem>
                </SelectContent>
              </Select>
            )}
          </div>

          <div className='grid gap-1.5'>
            <Label>SKU (optionnel)</Label>
            <Input
              value={form.sku}
              onChange={(e) => setForm((f) => ({ ...f, sku: e.target.value }))}
              placeholder='Code interne / barre'
            />
          </div>

          <div className='grid gap-1.5'>
            <Label>Prix unitaire (CFA)</Label>
            <Input
              inputMode='decimal'
              type='number'
              step='0.01'
              min={0}
              value={form.unitPrice}
              onChange={(e) =>
                setForm((f) => ({ ...f, unitPrice: e.target.value }))
              }
              placeholder='Ex: 500'
            />
          </div>

          <div className='grid gap-1.5'>
            <Label>Catégorie (optionnel)</Label>
            <Select
              value={form.categoryId || 'NONE'} // jamais une string vide
              onValueChange={(v) =>
                setForm((f) => ({ ...f, categoryId: v === 'NONE' ? '' : v }))
              }
            >
              <SelectTrigger>
                <SelectValue placeholder='Choisir…' />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value='NONE'>— Aucune —</SelectItem>
                {(cats ?? []).map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className='grid gap-1.5'>
            <Label>Conditionnement (optionnel)</Label>
            <Input
              value={form.packaging}
              onChange={(e) =>
                setForm((f) => ({ ...f, packaging: e.target.value }))
              }
              placeholder='Ex: carton de 24'
            />
          </div>
        </div>
      </AppDialog>

      {/* --- Modale CONFIRM SUPPRESSION --- */}
      <AppDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title='Supprimer le produit'
        description='Cette action est irréversible.'
        footer={
          <ModalActions
            confirmVariant='destructive'
            confirmLabel='Supprimer'
            confirmLoading={deleteMut.isPending}
            onConfirm={confirmDelete}
          />
        }
      >
        <div className='px-4 py-2 text-sm'>
          Voulez-vous vraiment supprimer ce produit ?
        </div>
      </AppDialog>

      <ImportItemsDialog open={importOpen} onOpenChange={setImportOpen} />
    </div>
  )
}
